import { createContext, useContext } from "react";

export type FetchClient = <T>(path: string, init?: RequestInit) => Promise<T>;

const FetchClientContext = createContext<FetchClient | undefined>(undefined);

export const FetchClientProvider = FetchClientContext.Provider;

export const useFetchWrapper = () => {
  const fetchClient = useContext(FetchClientContext);
  if (!fetchClient) {
    throw new Error(
      "useFetchWrapper must be used within a FetchClientProvider",
    );
  }
  return fetchClient;
};

export type ErrorType<ErrorData> = ErrorData;

export type BodyType<BodyData> = BodyData;

export class EnrichedFetchError extends Error {
  public readonly status?: number;
  public readonly body?: unknown;

  constructor(message: string, status?: number, body?: unknown) {
    super(message);
    this.name = "EnrichedFetchError";
    this.status = status;
    this.body = body;
    Object.setPrototypeOf(this, EnrichedFetchError.prototype);
  }
}

const BINARY_CONTENT_TYPES = [
  "image/",
  "application/zip",
  "application/octet-stream",
  "application/pdf",
  "text/csv",
];

const isBinary = (contentType: string) =>
  BINARY_CONTENT_TYPES.some((type) => contentType.includes(type));

export const readResponse = async <T>(
  response: Response,
  path: string,
): Promise<T> => {
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    throw new EnrichedFetchError(
      `Error when calling ${path}. Status: ${response.status}.`,
      response.status,
      body,
    );
  }

  if (response.status === 204) {
    return {} as T;
  }

  const contentType = response.headers.get("Content-Type") ?? "";

  return (
    isBinary(contentType) ? response.blob() : response.json()
  ) as Promise<T>;
};
